import { getPriceHistory } from '@simplywallst/services';
import type { QueryFunctionContext } from '@tanstack/react-query';

export interface Price {
  date: number | null;
  price: number | null;
}

export interface QueryResponse {
  data: {
    Company?: {
      priceCloses?: Price[];
    };
  };
}

export enum Outcome {
  NEUTRAL = 'url(#positive-chart)',
  POSITIVE = 'url(#positive-chart)',
  NEGATIVE = 'url(#negative-chart)',
}

export interface SelectorResponse {
  keys: number[];
  entities: Record<number, number | null>;
  minY: number;
  maxY: number;
  movement: Outcome;
}

export const getDefault = (): SelectorResponse => ({
  keys: [],
  entities: {},
  minY: 0,
  maxY: 0,
  movement: Outcome.NEUTRAL,
});

export const reducer = (data: Price[] = []): SelectorResponse => {
  const entities: Record<number, number | null> = {};
  const keys = data.reduce<number[]>((a, b) => {
    if (b.date === null) return a;
    entities[b.date] = b.price;
    return [...a, b.date];
  }, []);

  let minY = 0;
  let maxY = 0;
  if (keys.length > 0) {
    const prices: number[] = keys.reduce<number[]>((a, b) => {
      const price = entities[b];
      if (price === null) return a;
      return [...a, price];
    }, []);
    minY = Math.min(...prices);
    maxY = Math.max(...prices);
  }

  let movement = Outcome.NEUTRAL;
  const firstY = entities[keys[0]];
  const lastY = entities[keys[keys.length - 1]];
  if (firstY !== null && lastY !== null) {
    if (firstY > lastY) movement = Outcome.NEGATIVE;
    if (firstY < lastY) movement = Outcome.POSITIVE;
    if (firstY === lastY) movement = Outcome.POSITIVE;
  }

  return {
    keys,
    entities,
    minY,
    maxY,
    movement,
  };
};

const queryFn = async ({
  queryKey: [_key, companyId, start, end],
}: QueryFunctionContext<
  [string, string, number, number | undefined]
>): Promise<SelectorResponse> => {
  try {
    if (typeof companyId === 'undefined') {
      throw new Error('lookup value is undefined');
    }
    const res = await getPriceHistory({
      companyId,
      payload: {
        start_timestamp: start,
        ...(end !== undefined && { end_timestamp: end }),
      },
    });
    const { data } = res;
    if (data) {
      const timeseriesData = data.data
        .map((n) => ({
          date: n.date,
          price: n.close,
        }))
        .reverse();
      return reducer(timeseriesData);
    }
    return getDefault();
  } catch (error) {
    throw error;
  }
};

export default queryFn;
